/**
 * Single source of truth for the `GeneticInfoDTO` enum value sets
 * (PTV2-1172). Values are the exact wire codes the backend accepts; unknown
 * values are rejected with 422, so the form only ever offers what lives here.
 *
 * The `"na"` unset sentinel is intentionally absent — an unselected control
 * maps to `na` (or an omitted key) in `variantForm.draftToDto`.
 *
 * Labels: semantic enums carry an i18n `labelKey`
 * (`patient.geneticInfo.options.*`); pure identifiers (chromosome, genome
 * build) carry a literal `label` since they are proper nouns, not prose.
 */

export interface FieldOption {
  readonly value: string;
  /** i18n key under `patient.geneticInfo.options.*`. */
  readonly labelKey?: string;
  /** Literal label for non-translatable identifiers (chromosome, genome). */
  readonly label?: string;
}

const OPT = 'patient.geneticInfo.options';

function keyed(field: string, values: readonly string[]): readonly FieldOption[] {
  return values.map((value) => ({ value, labelKey: `${OPT}.${field}.${value}` }));
}

/** Variant type — drives which conditional field groups are shown. */
export const VARIANT_TYPE_OPTIONS = keyed('variantType', [
  'snv',
  'indel',
  'cnv',
  'mitochondrial',
  'structural',
  'repeat_expansion',
]);

export const REFERENCE_GENOME_OPTIONS: readonly FieldOption[] = [
  { value: 'GRCh38', label: 'GRCh38 (hg38)' },
  { value: 'GRCh37', label: 'GRCh37 (hg19)' },
  { value: 'NCBI36', label: 'NCBI36 (hg18)' },
  { value: 'GRCh39', label: 'GRCh39' },
];

/** Nice-to-have default per the FINAL field spec. */
export const REFERENCE_GENOME_DEFAULT = 'GRCh38';

export const CHROMOSOME_OPTIONS: readonly FieldOption[] = [
  ...Array.from({ length: 22 }, (_, i) => {
    const v = String(i + 1);
    return { value: v, label: v };
  }),
  { value: 'X', label: 'X' },
  { value: 'Y', label: 'Y' },
  { value: 'MT', label: 'MT' },
];

export const INTERPRETATION_OPTIONS = keyed('interpretation', [
  'pathogenic',
  'predisposing',
  'likely_pathogenic',
  'likely_predisposing',
  'vus',
  'likely_benign',
  'benign',
  'negative',
  'positive',
  'investigation_needed',
]);

export const STATUS_OPTIONS = keyed('status', [
  'candidate',
  'rejected_candidate',
  'confirmed_casual',
  'carrier',
  'tested_negative',
]);

export const ZYGOSITY_SNV_OPTIONS = keyed('zygositySnv', [
  'heterozygous',
  'homozygous',
  'hemizygous',
]);

export const CNV_TYPE_OPTIONS = keyed('cnvType', [
  'deletion',
  'duplication',
  'amplification',
  'loss',
  'gain',
]);

export const CNV_CLASSIFICATION_OPTIONS = keyed('cnvClassification', [
  'unclassified',
  'copy_number_polymorphism',
  'clinically_significant',
]);

export const CNV_STRUCTURAL_IMPACT_OPTIONS = keyed('cnvStructuralImpact', [
  'whole_gene',
  'partial_gene',
  'multi_gene',
  'intergenic',
]);

export const CNV_FUNCTIONAL_IMPACT_OPTIONS = keyed('cnvFunctionalImpact', [
  'loss_of_function',
  'gain_of_function',
  'dosage_sensitivity',
  'benign_polymorphic_gain',
  'uncertain_significance',
]);

export const ZYGOSITY_MTDNA_OPTIONS = keyed('zygosityMtdna', [
  'homoplasmic',
  'heteroplasmic',
  'mosaic',
]);

export const STRUCTURAL_TYPE_OPTIONS = keyed('structuralType', [
  'deletion',
  'duplication',
  'inversion',
  'translocation',
]);

/** Reuses the existing `patient.geneticInfo.inheritance.*` labels (no dupes). */
export const INHERITANCE_OPTIONS: readonly FieldOption[] = [
  'de_novo_germline',
  'de_novo_s_mosaicism',
  'maternal',
  'paternal',
  'biparental',
  'unknown',
].map((value) => ({ value, labelKey: `patient.geneticInfo.inheritance.${value}` }));

export const SANGER_VALIDATION_OPTIONS = keyed('sangerValidation', [
  'positive',
  'negative',
]);

export const SEGREGATION_OPTIONS = keyed('segregation', [
  'segregates',
  'does_not_segregates',
]);

/** VEP consequence terms — multi-select. */
export const EFFECT_OPTIONS = keyed('effect', [
  'transcript_ablation',
  'splice_acceptor_variant',
  'splice_donor_variant',
  'stop_gained',
  'frameshift_variant',
  'stop_lost',
  'start_lost',
  'missense_variant',
  'inframe_insertion',
  'inframe_deletion',
  'protein_altering_variant',
  'synonymous_variant',
  'splice_region_variant',
  'initiator_codon_variant',
  'stop_retained_variant',
  'coding_sequence_variant',
  'intron_variant',
  'upstream_gene_variant',
  'downstream_gene_variant',
  'regulatory_region_variant',
  'intergenic_variant',
  'tf_binding_site_variant',
  'mature_mirna_variant',
  'non_coding_transcript_exon_variant',
]);

export const STRATEGY_OPTIONS = keyed('strategy', [
  'sequencing',
  'deletion_dup',
  'familial_mutation',
  'common_mutations',
]);

export const EVIDENCE_OPTIONS = keyed('evidence', [
  'rare',
  'predicted_damaging',
  'reported_in_others',
]);
